import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AppState } from '@parts/shared';
import { errorText, getJson } from './api';

/**
 * Общее состояние: снимок расчёта и журнал действий.
 * Обновляется после каждого проведения — экраны берут свежие данные отсюда.
 */
interface Ctx {
  state: AppState | null;
  error: string | null;
  reload: () => Promise<void>;
  apply: (state: AppState) => void;
}

const StateContext = createContext<Ctx | null>(null);

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setState(await getJson<AppState>('/api/state'));
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const value = useMemo<Ctx>(() => ({ state, error, reload, apply: setState }), [state, error, reload]);
  return <StateContext.Provider value={value}>{children}</StateContext.Provider>;
}

export function useAppState(): Ctx {
  const ctx = useContext(StateContext);
  if (!ctx) throw new Error('AppStateProvider не подключён');
  return ctx;
}
